//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { TrackingParam } from '@apis/universal-image-actions/universalImageActionsApiModel';
import { PrimaryLinkButton } from '@components/atoms/button/Button';
import { Caption } from '@components/atoms/caption/Caption';
import { InlineSpinner } from '@components/atoms/spinner/Spinner';
import {
  DistributionTouchpoint,
  UiaProductTrackingIds,
} from '@uia/products/constants/UiaProductConstants';
import { ProductLoadingError } from '@uia/products/editor/curalate-product-editor/components/ProductLoadingError';
import { useTrackingUrlPreviewQuery } from '@uia/products/hooks/useTrackingUrlPreviewQuery';

interface ProductPreviewUrlProps {
  productUrl: string;
  trackingParamsVersion: number;
  overrideParamGroups: TrackingParam[];
  setIsEditing: () => void;
}

export const ProductPreviewUrlAndEditCta = ({
  overrideParamGroups,
  productUrl,
  setIsEditing,
  trackingParamsVersion,
}: ProductPreviewUrlProps) => {
  const {
    data: previewUrl,
    isError,
    isPending,
  } = useTrackingUrlPreviewQuery({
    url: productUrl,
    autoFills: {
      distributionTouchpoint: DistributionTouchpoint.CuralateTouchpoint,
    },
    paramsVersionId: trackingParamsVersion,
    overrideParams: overrideParamGroups,
  });

  if (isPending) {
    return <InlineSpinner />;
  }

  if (isError || !previewUrl) {
    return <ProductLoadingError />;
  }

  return (
    <>
      <PreviewUrl
        caption={previewUrl}
        maxLineCount={4}
        shouldDecorateLinks={false}
      />
      <PrimaryLinkButton
        isDisabled={isPending}
        onClick={setIsEditing}
        trackingId={
          UiaProductTrackingIds.PRODUCT_EDITOR_EDIT_URL_TRACKING_PARAMS
        }
      >
        Edit URL & Tracking Parameters
      </PrimaryLinkButton>
    </>
  );
};

const PreviewUrl = styled(Caption)`
  word-break: break-all;
`;
